import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, IconButton, Stack } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { LoopClock } from '@/designs-dev/handover/Looping';

/**
 * Controls for a looping mini, above it rather than in the phone: a bar
 * showing where in the loop it is, play and pause, and whether it loops. The
 * bar is what explains a loop that starts with a jump: you can see it is the
 * start.
 *
 * The bar is the clock. Each time it fills, the screen's Looping plays again,
 * so pausing the bar pauses the loop. Pausing also freezes the screen's own
 * animations and transitions where they are. Timers in the prototype carry on,
 * so a step that waits and then moves will still move.
 *
 * With looping off it plays once and stops on its last frame; play runs it
 * again. Shows nothing extra around a screen that does not loop.
 */
const LoopFrame = ({
  width,
  children,
}: {
  /** The mini's width, so the controls line up under it. */
  width: number;
  children: ReactNode;
}) => {
  const [every, setEvery] = useState<number>();
  const [run, setRun] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [looping, setLooping] = useState(true);
  const [ended, setEnded] = useState(false);
  const screen = useRef<HTMLDivElement>(null);
  const frozen = useRef<Animation[]>([]);

  const clock = useMemo(() => ({ run, register: setEvery }), [run]);

  useEffect(() => {
    if (playing) {
      frozen.current.forEach((a) => a.play());
      frozen.current = [];
      return;
    }
    const running = (
      screen.current?.getAnimations({ subtree: true }) ?? []
    ).filter((a) => a.playState === 'running');
    running.forEach((a) => a.pause());
    frozen.current = running;
  }, [playing]);

  const onFilled = () => {
    if (looping) {
      setRun((n) => n + 1);
    } else {
      setPlaying(false);
      setEnded(true);
    }
  };

  const onPlay = () => {
    if (ended) {
      frozen.current = [];
      setEnded(false);
      setRun((n) => n + 1);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  };

  return (
    <Stack spacing={1} sx={{ width }}>
      {/* Above the screen rather than under it: under, it sat wherever the
          caption ended, so a row of loops had its controls at different
          heights. */}
      {every && (
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
          <IconButton
            size='small'
            aria-label={playing ? 'Pause' : 'Play'}
            onClick={onPlay}
          >
            <Icon icon={(playing ? 'pause' : 'play') as never} fontSize={14} />
          </IconButton>
          <IconButton
            size='small'
            aria-label='Loop'
            aria-pressed={looping}
            onClick={() => setLooping((l) => !l)}
            sx={{ color: looping ? 'primary.main' : 'text.disabled' }}
          >
            <Icon icon={'repeat' as never} fontSize={14} />
          </IconButton>
          <Box
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              // A new bar each play, so the fill starts again from empty.
              key={run}
              onAnimationEnd={onFilled}
              sx={{
                height: '100%',
                bgcolor: 'primary.main',
                transformOrigin: 'left',
                '@keyframes loopFill': {
                  from: { transform: 'scaleX(0)' },
                  to: { transform: 'scaleX(1)' },
                },
                animation: `loopFill ${every}ms linear forwards`,
                animationPlayState: playing ? 'running' : 'paused',
              }}
            />
          </Box>
        </Stack>
      )}
      <Box ref={screen}>
        <LoopClock.Provider value={clock}>{children}</LoopClock.Provider>
      </Box>
    </Stack>
  );
};

export default LoopFrame;
